import { Table, ScrollArea, Flex, ActionIcon, Box } from "@mantine/core";
import { useUnit } from "effector-react";
import { useState } from 'react';
import { articleDetailsEntityModel } from "../../model/details";
import {
  IconPhotoDown,
  IconExternalLink,
  IconTrash,
} from "@tabler/icons-react";
import { downloadImage } from "../../../../shared/libs/download-image";
import { getColorFromString} from "../../../../shared/libs/get-color-from-string";
import { ArticleDetails } from "../../types";

const parseCustomDate = (dateString: string): Date => {
  const [datePart, timePart] = dateString.split(' ');
  const [day, month, year] = datePart.split('/').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);
  // Note: Month is 0-indexed in JavaScript Date
  return new Date(year, month - 1, day, hours, minutes);
};

export const ArticleResultsTable = () => {
  const [sortField, setSortField] = useState<'domain' | 'date' | 'title' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const [data, deleteSimilarArticleById] = useUnit([
    articleDetailsEntityModel.$data,
    articleDetailsEntityModel.deleteSimilarArticleById,
  ]);

  const [sortedData, setSortedData] = useState<ArticleDetails | null>(data);

  const getSortIcon = (field: 'domain' | 'date' | 'title') => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const handleSort = (field: 'domain' | 'date' | 'title') => {
    if (sortedData === null) {
      return
    }

    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';

    setSortField(field);
    setSortDirection(newDirection);

    // Sort the data
    const similarities = [...sortedData.similarities].sort((a, b) => {
      const aValue = a[field];
      const bValue = b[field];

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return newDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {

        if (field === 'domain') {
          const comparison = String(aValue).localeCompare(String(bValue));
          return newDirection === 'asc' ? comparison : -comparison;
        }

        const isDateString = /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/.test(aValue) && /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/.test(bValue);
        if (isDateString) {
          const comparison = parseCustomDate(aValue).getTime() - parseCustomDate(bValue).getTime();
          return newDirection === 'asc' ? comparison : -comparison;
        }
      }

      // String comparison
      const comparison = String(aValue).localeCompare(String(bValue));
      return newDirection === 'asc' ? comparison : -comparison;
    });

    sortedData.similarities = similarities;
    setSortedData(sortedData);
  };

  return (
    <Box>
      <ScrollArea>
        <Table stickyHeader>
          <Table.Thead>
            <Table.Tr>
              <Table.Th p="md" onClick={() => handleSort('domain')} style={{ cursor: 'pointer' }}>Domain {getSortIcon('domain')}</Table.Th>
              <Table.Th p="md" onClick={() => handleSort('date')} style={{ cursor: 'pointer' }}>Date {getSortIcon('date')}</Table.Th>
              <Table.Th p="md" onClick={() => handleSort('title')} style={{ cursor: 'pointer' }}>Title {getSortIcon('title')}</Table.Th>
              <Table.Th p="md">Snippet</Table.Th>
              <Table.Th p="md">Search engine</Table.Th>
              <Table.Th p="md">Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {sortedData?.similarities.map((item) => (
              <Table.Tr key={item.id}>
                <Table.Td maw={200} p="md" style={{ backgroundColor: item.count > 1 ? getColorFromString(item.domain) : ''}}>
                  <a href={item.url} target="_blank" rel="noopener noreferrer">
                    {item.domain}
                  </a>
                </Table.Td>
                <Table.Td maw={350} p="md">
                  {item.date}
                </Table.Td>
                <Table.Td maw={350} p="md">
                  {item.title}
                </Table.Td>
                <Table.Td maw={350} p="md">
                  {item.snippet}
                </Table.Td>
                <Table.Td p="md">{item.searchEngine}</Table.Td>
                <Table.Td p="md">
                  <Flex w="100%" align="center" gap="xs">
                    {item.screenshotId && (
                      <ActionIcon
                        variant="subtle"
                        title="Скачать скриншот"
                        onClick={() =>
                          downloadImage(
                            `/images/screenshots/${item.screenshotId}.png`,
                            `screenshot.png`
                          )
                        }
                      >
                        <IconPhotoDown />
                      </ActionIcon>
                    )}

                    <ActionIcon
                      variant="subtle"
                      title="Открыть страницу"
                      onClick={() => window.open(item.url, "_blank")}
                    >
                      <IconExternalLink />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      title="Удалить статью"
                      onClick={() => deleteSimilarArticleById(item.id)}
                    >
                      <IconTrash />
                    </ActionIcon>
                  </Flex>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Box>
  );
};
